import React from 'react';
import { Calendar, MapPin, Mail, Clock } from 'lucide-react';
import { asset } from '../utils/asset';

const AdmissionPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-6">📢 EARLY ADMISSION ANNOUNCEMENT</h1>
            <p className="text-xl text-blue-100">
              The Guidance Office is pleased to announce that Early Admission for the upcoming school year is now open.
            </p>
          </div>
        </div>
      </section>

      {/* Admission Schedule */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">📅 ADMISSION SCHEDULE</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Phase 1 */}
            <div className="card">
              <div className="mb-6">
                <img 
                  src={asset("/images/admission_phase1.jpg")} 
                  alt="Phase 1 Schedule" 
                  className="w-full rounded-lg shadow-md"
                />
              </div>
              <h3 className="text-2xl font-bold text-blue-800 mb-4">𝗣𝗵𝗮𝘀𝗲 𝟭: 𝗙𝗲𝗯𝗿𝘂𝗮𝗿𝘆</h3>
              <p className="text-gray-600 mb-4">
                During this early period, the Guidance Office will be open to accommodate all applicants on a limited schedule.
              </p>
              <div className="space-y-2 text-gray-700">
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-blue-800 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Days:</strong> Fridays and Saturdays Only
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-blue-800 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Service Hours:</strong>
                    <br />• Friday: 8:30 AM – 4:30 PM
                    <br />• Saturday: 8:30 AM – 11:30 AM
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 2 */}
            <div className="card">
              <div className="mb-6 space-y-4">
                <img 
                  src={asset("/images/admission_phase2a.jpg")} 
                  alt="Phase 2 Schedule Part A" 
                  className="w-full rounded-lg shadow-md"
                />
                <img 
                  src={asset("/images/admission_phase2b.jpg")} 
                  alt="Phase 2 Schedule Part B" 
                  className="w-full rounded-lg shadow-md"
                />
              </div>
              <h3 className="text-2xl font-bold text-blue-800 mb-4">𝗣𝗵𝗮𝘀𝗲 𝟮: 𝗠𝗮𝗿𝗰𝗵 𝘁𝗼 𝗠𝗮𝘆</h3>
              <p className="text-gray-600 mb-4">
                Starting in March, the schedule will expand to accommodate specific departments on designated days.
              </p>
              <div className="space-y-2 text-gray-700 mb-4">
                <div className="flex items-start">
                  <Calendar className="w-5 h-5 text-blue-800 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Days:</strong> Mondays to Saturdays
                  </div>
                </div>
                <div className="flex items-start">
                  <Clock className="w-5 h-5 text-blue-800 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Service Hours:</strong>
                    <br />• Monday to Friday: 8:30 AM – 4:30 PM
                    <br />• Saturday: 8:30 AM – 12:00 NN
                  </div>
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-bold text-blue-800 mb-2">Department Schedule:</h4>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>🔹 <strong>Mon:</strong> Basic Education (BED)</li>
                  <li>🔹 <strong>Tue:</strong> Senior High School (SHS)</li>
                  <li>🔹 <strong>Wed:</strong> Higher Education (College)</li>
                  <li>🔹 <strong>Thu:</strong> Interviews (BED & SHS)</li>
                  <li>🔹 <strong>Fri:</strong> Interviews (Higher Ed)</li>
                  <li>🔹 <strong>Sat:</strong> Graduate School</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-16">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">📝 APPLICATION PROCESS</h2>
          </div>

          <div className="mb-12">
            <img 
              src={asset("/images/process_of_application.jpg")} 
              alt="Application Process" 
              className="w-full max-w-4xl mx-auto rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-lg font-bold mb-3">𝗚𝗘𝗧 𝗬𝗢𝗨𝗥 𝗙𝗢𝗥𝗠</h3>
              <p className="text-gray-600 text-sm">
                Secure and accomplish an application form for the Entrance Exam at the <strong>Guidance Center</strong>.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-lg font-bold mb-3">𝗦𝗨𝗕𝗠𝗜𝗧 𝗥𝗘𝗤𝗨𝗜𝗥𝗘𝗠𝗘𝗡𝗧𝗦</h3>
              <p className="text-gray-600 text-sm mb-3">
                Submit your form and the following to the <strong>Accounting Office</strong>:
              </p>
              <ul className="text-xs text-gray-600 text-left space-y-1">
                <li>• Two (2) copies of 2x2 I.D. picture (White background)</li>
                <li>• Photocopy of Form-138/Report Card (at least 2nd Quarter) or Transcript of Records (TOR)</li>
              </ul>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-lg font-bold mb-3">𝗣𝗔𝗬𝗠𝗘𝗡𝗧</h3>
              <p className="text-gray-600 text-sm">
                Pay the Entrance Exam Fee at the <strong>Accounting Office</strong>.
              </p>
            </div>

            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-800 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                4
              </div>
              <h3 className="text-lg font-bold mb-3">𝗦𝗖𝗛𝗘𝗗𝗨𝗟𝗘 𝗘𝗫𝗔𝗠</h3>
              <p className="text-gray-600 text-sm">
                Return to the <strong>Guidance Center</strong> to secure your Entrance Exam date.
              </p>
            </div>
          </div>

          <div className="mt-8 p-6 bg-yellow-50 border-l-4 border-yellow-500 rounded-lg">
            <h4 className="font-bold text-yellow-800 mb-2">💡 NOTE FOR TRANSFEREES/RETURNEES:</h4>
            <p className="text-yellow-700">
              Please proceed directly to the Principal's or Coordinator's Office for evaluation and interview before proceeding.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="card max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-blue-800 mb-6">Contact Information</h3>
            <div className="space-y-4 text-gray-700">
              <div className="flex items-center justify-center">
                <MapPin className="w-5 h-5 text-blue-800 mr-3" />
                <div>
                  <strong>Location:</strong> Guidance Center, BED Building<br />
                  Burgos Street, Talisay, 4700 Sorsogon, Sorsogon
                </div>
              </div>
              <div className="flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-800 mr-3" />
                <span><strong>Inquiries:</strong> slmcs.gcs@gmail.com</span>
              </div>
            </div>
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 font-bold text-lg">
                𝘿𝙤𝙣'𝙩 𝙬𝙖𝙞𝙩 𝙪𝙣𝙩𝙞𝙡 𝙩𝙝𝙚 𝙡𝙖𝙨𝙩 𝙢𝙞𝙣𝙪𝙩𝙚—𝙨𝙚𝙚 𝙮𝙤𝙪 𝙤𝙣 𝙘𝙖𝙢𝙥𝙪𝙨!
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdmissionPage;
